import { type ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';

export interface HeatmapColumn {
  key: string;
  label: string;
  /** Optional color swatch shown in the header (e.g. scope color) */
  color?: string;
}

export interface HeatmapRow {
  id: string;
  label: ReactNode;
  /** Keyed by column key → CO2e value in kg */
  values: Record<string, number>;
  total: number;
}

interface HeatmapGridProps {
  title: string;
  description?: string;
  columns: HeatmapColumn[];
  rows: HeatmapRow[];
  isLoading?: boolean;
  emptyMessage?: string;
  /** Extra content rendered in the card header (e.g. a toggle) */
  headerExtra?: ReactNode;
}

const getHeatColor = (value: number, max: number): string => {
  if (value === 0 || max === 0) return 'transparent';
  const ratio = Math.min(value / max, 1);
  // Low values → green (120°), high values → red (0°)
  const hue = 120 * (1 - ratio);
  const alpha = 0.2 + ratio * 0.6;
  return `hsla(${hue}, 80%, 42%, ${alpha})`;
};

export const HeatmapGrid = ({ title, description, columns, rows, isLoading, emptyMessage, headerExtra }: HeatmapGridProps) => {
  const { formatCompact } = useEmissionsFormatter();

  if (isLoading) {
    return <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8'>Loading heatmap data...</p>;
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-12'>
            {emptyMessage ?? 'No data found for this period.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Find max cell value for color scaling and compute column totals
  let maxValue = 0;
  const columnTotals: Record<string, number> = {};
  let grandTotal = 0;
  for (const row of rows) {
    grandTotal += row.total;
    for (const col of columns) {
      const v = row.values[col.key] ?? 0;
      if (v > maxValue) maxValue = v;
      columnTotals[col.key] = (columnTotals[col.key] ?? 0) + v;
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className='tw:flex tw:items-center tw:justify-between tw:gap-4'>
          <CardTitle>{title}</CardTitle>
          {headerExtra}
        </div>
        {description && (
          <CardDescription>{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className='tw:overflow-x-auto'>
        <table className='tw:w-full'>
          <thead>
            <tr>
              <th className='tw:text-left tw:px-2 tw:py-2 tw:min-w-[140px]'>Name</th>
              {columns.map((col) => (
                <th key={col.key} className='tw:text-center tw:px-2 tw:py-2 tw:min-w-[80px]'>
                  <div className='tw:flex tw:items-center tw:justify-center tw:gap-1'>
                    {col.color && (
                      <span className='tw:rounded tw:shrink-0' style={{ width: 8, height: 8, backgroundColor: col.color }} />
                    )}
                    <span className='tw:leading-tight'>{col.label}</span>
                  </div>
                </th>
              ))}
              <th className='tw:text-right tw:px-2 tw:py-2'>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className='tw:border-t'>
                <td className='tw:px-2 tw:py-2'>{row.label}</td>
                {columns.map((col) => {
                  const cellValue = row.values[col.key] ?? 0;
                  const cellFormatted = cellValue > 0 ? formatCompact(cellValue) : null;
                  return (
                    <td
                      key={col.key}
                      className='tw:text-center tw:px-2 tw:py-2 tw:text-md'
                      style={{ backgroundColor: getHeatColor(cellValue, maxValue) }}
                      title={cellFormatted ? cellFormatted.display : 'No data'}
                    >
                      {cellFormatted ? (
                        <span>
                          {cellFormatted.formatted}
                          <span className='tw:text-xs tw:opacity-70 tw:ml-0.5'>{cellFormatted.unitCompact}</span>
                        </span>
                      ) : (
                        <span className='tw:text-muted-foreground/30'>—</span>
                      )}
                    </td>
                  );
                })}
                <td className='tw:text-right tw:px-2 tw:py-2'>
                  {formatCompact(row.total).display}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className='tw:border-t-2'>
              <td className='tw:px-2 tw:py-2 tw:font-semibold'>Total</td>
              {columns.map((col) => {
                const colTotal = columnTotals[col.key] ?? 0;
                const colTotalFormatted = colTotal > 0 ? formatCompact(colTotal) : null;
                return (
                  <td key={col.key} className='tw:text-center tw:px-2 tw:py-2 tw:font-semibold'>
                    {colTotalFormatted ? (
                      <span>
                        {colTotalFormatted.formatted}
                        <span className='tw:text-xs tw:opacity-70 tw:ml-0.5'>{colTotalFormatted.unitCompact}</span>
                      </span>
                    ) : (
                      <span className='tw:text-muted-foreground/30'>—</span>
                    )}
                  </td>
                );
              })}
              <td className='tw:text-right tw:px-2 tw:py-2 tw:font-semibold'>
                {formatCompact(grandTotal).display}
              </td>
            </tr>
          </tfoot>
        </table>
      </CardContent>
    </Card>
  );
};

export type HeatmapMode = 'scope' | 'activity';

const HEATMAP_MODE_TABS: TabItem<HeatmapMode>[] = [
  { key: 'scope', label: 'By Scope' },
  { key: 'activity', label: 'By Activity Type' },
];

export const HeatmapModeToggle = ({ value, onChange }: { value: HeatmapMode; onChange: (v: HeatmapMode) => void }) => (
  <div className='tw:flex tw:items-center tw:gap-2'>
    <span className='tw:text-muted-foreground'>Columns:</span>
    <TabNav
      size='xs'
      variant='toggle'
      tabs={HEATMAP_MODE_TABS}
      activeTab={value}
      onChange={onChange}
    />
  </div>
);
